/**
 * Keybinding string parser.
 *
 * Translates human-readable keybinding strings (e.g. `"cmd+k"`, `"ctrl+d"`)
 * into `KeyboardEvent` matcher functions. This abstraction frees shortcut
 * consumers from manually inspecting `metaKey`, `ctrlKey`, `shiftKey`, etc.
 *
 * Supported modifier tokens (case-insensitive):
 *   - `cmd`   → `metaKey` (⌘ on macOS)
 *   - `ctrl`  → `ctrlKey`
 *   - `shift` → `shiftKey`
 *   - `alt`   → `altKey`
 *
 * The last token is always treated as the non-modifier key and is matched
 * against `event.key` (lowercased).
 *
 * @example
 * ```ts
 * const match = parseKeybinding('cmd+k');
 * // match({ metaKey: true, key: 'k', ... }) → true
 * ```
 */
const MODIFIER_MAP = {
    cmd: 'meta',
    meta: 'meta',
    ctrl: 'ctrl',
    control: 'ctrl',
    shift: 'shift',
    alt: 'alt',
    option: 'alt',
};
/**
 * Parses a keybinding string into modifiers plus a final non-modifier key.
 *
 * Modifier aliases are accepted case-insensitively: `cmd`/`meta`,
 * `ctrl`/`control`, `alt`/`option`, and `shift`. Unknown non-final tokens throw
 * so descriptors cannot silently ignore unsupported modifiers.
 *
 * @throws If the descriptor is empty, has no final key, or contains an unknown
 * non-final modifier token.
 */
function parseKeybindingDescriptor(keybinding) {
    const tokens = keybinding
        .toLowerCase()
        .split('+')
        .map((t) => t.trim())
        .filter(Boolean);
    if (tokens.length === 0) {
        throw new Error(`Invalid keybinding: "${keybinding}" — must contain at least one key`);
    }
    const parsed = {
        meta: false,
        ctrl: false,
        shift: false,
        alt: false,
        key: '',
    };
    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        const modifier = MODIFIER_MAP[token];
        if (modifier) {
            parsed[modifier] = true;
        }
        else if (i === tokens.length - 1) {
            // Last token is the non-modifier key
            parsed.key = token;
        }
        else {
            throw new Error(`Invalid keybinding: "${keybinding}" — unknown modifier "${token}". ` +
                `Supported modifiers: ${Object.keys(MODIFIER_MAP).join(', ')}`);
        }
    }
    if (!parsed.key) {
        throw new Error(`Invalid keybinding: "${keybinding}" — must end with a non-modifier key`);
    }
    return parsed;
}
/**
 * Returns the lowercase `event.code` for a digit key (e.g. `'1'` → `'digit1'`),
 * or `undefined` for non-digit keys.
 *
 * Used as a fallback when modifier keys transform `event.key`
 * (e.g. Shift+1 → `"!"`, Alt+1 → `"¡"`).
 */
function digitCode(key) {
    return key.length === 1 && key >= '0' && key <= '9' ? `digit${key}` : undefined;
}
/**
 * Returns the lowercase `event.code` for a single ASCII letter key
 * (e.g. `'u'` → `'keyu'`), or `undefined` for non-letter keys.
 *
 * Used as a physical fallback when a modifier+letter shortcut is pressed on a
 * layout that does not report an ASCII Latin `event.key` (e.g. Russian:
 * physical "U" produces `"г"` as `event.key`, while `event.code` remains
 * `"KeyU"`).
 */
function alphaCode(key) {
    return key.length === 1 && key >= 'a' && key <= 'z' ? `key${key}` : undefined;
}
/**
 * Discriminates remapped Latin layouts from single-alphabet non-Latin layouts.
 *
 * Dvorak/Colemak can report a different Latin `event.key` at the same physical
 * key position, so the resolved key must win. Russian/Arabic/Greek layouts
 * report non-Latin keys, so modifier+letter shortcuts can safely fall back to
 * `event.code`.
 */
function isAsciiLatinLetterKey(key) {
    return key !== undefined && key.length === 1 && key >= 'a' && key <= 'z';
}
function eventCodeMatches(event, code) {
    return code !== undefined && event.code?.toLowerCase() === code;
}
/**
 * Matches modifier shortcuts using `event.key` first, with selective
 * `event.code` fallback.
 *
 * Examples for `ctrl+u`:
 * - English `{ key: 'u', code: 'KeyU' }` matches via `event.key`.
 * - Dvorak paste `{ key: 'v', code: 'KeyU' }` does not match, preventing
 *   WEB-2520's remapped Ctrl+V file-upload bug.
 * - Russian `{ key: 'г', code: 'KeyU' }` matches via physical-code fallback.
 * - `Dead`, `Unidentified`, and `""` fall back to physical code.
 */
function modifierKeyMatches(event, key) {
    const eventKey = event.key?.toLowerCase();
    const digitFallbackCode = digitCode(key);
    if (digitFallbackCode !== undefined) {
        return eventKey === key || eventCodeMatches(event, digitFallbackCode);
    }
    const alphaFallbackCode = alphaCode(key);
    if (alphaFallbackCode !== undefined) {
        return isAsciiLatinLetterKey(eventKey)
            ? eventKey === key
            : eventCodeMatches(event, alphaFallbackCode);
    }
    return eventKey === key;
}
const BARE_CLIPBOARD_SHORTCUT_KEYS = new Set(['a', 'c', 'v', 'x']);
const BARE_CLIPBOARD_SHORTCUT_CODES = new Set(['keya', 'keyc', 'keyv', 'keyx']);
const UNDO_REDO_SHORTCUT_KEY = 'z';
const UNDO_REDO_SHORTCUT_CODE = 'keyz';
/**
 * Lets the browser/editor own native clipboard-style chords.
 *
 * Requires Cmd/Ctrl and never guards when Alt is held, because Alt-layered
 * clipboard-letter chords are not native clipboard operations and should fall
 * through to app shortcuts. Checks both resolved `event.key` for Latin layouts
 * and physical `event.code` for non-Latin layouts. Otherwise a Cyrillic
 * select-all `{ key: 'ф', code: 'KeyA' }` would miss a key-only guard and could
 * fire an app shortcut through the non-Latin code fallback.
 *
 * Shift is scoped to browser/editor behavior: select-all/copy/paste/cut
 * (`a`/`c`/`v`/`x`) are guarded only as bare Cmd/Ctrl chords, while `z` is
 * guarded with or without Shift so both undo and redo remain native.
 */
export function isBrowserClipboardShortcut(event) {
    const eventKey = event.key?.toLowerCase();
    const eventCode = event.code?.toLowerCase();
    if (!(event.metaKey || event.ctrlKey) || event.altKey) {
        return false;
    }
    const isUndoRedoShortcut = eventKey === UNDO_REDO_SHORTCUT_KEY || eventCode === UNDO_REDO_SHORTCUT_CODE;
    if (isUndoRedoShortcut) {
        return true;
    }
    return (!event.shiftKey &&
        (BARE_CLIPBOARD_SHORTCUT_KEYS.has(eventKey ?? '') ||
            BARE_CLIPBOARD_SHORTCUT_CODES.has(eventCode ?? '')));
}
/**
 * Creates a `KeyboardEvent` matcher from a keybinding string.
 *
 * The matcher checks that exactly the specified modifiers are pressed (no more,
 * no less) and that the non-modifier key matches.
 *
 * For **bare-key** keybindings (no modifiers at all), `shiftKey` is ignored.
 * Some keyboard layouts require Shift to produce certain characters (e.g. `/`),
 * and `event.key` already resolves to the actual character regardless of
 * whether Shift was involved. For modifier combos (e.g. `cmd+k`), all
 * modifiers — including Shift — are still enforced strictly.
 *
 * For **modifier+digit** keybindings (e.g. `alt+cmd+1`, `shift+ctrl+2`),
 * the matcher also checks `event.code` as a fallback. This is necessary
 * because Shift transforms digit keys into symbols (`!`, `@`, `#`, …)
 * and Alt transforms them into special characters (`¡`, `™`, …) on most
 * layouts, changing `event.key`. The `event.code` remains `"Digit1"`
 * regardless of modifier state.
 *
 * For **modifier+letter** keybindings (e.g. `ctrl+u`, `cmd+k`), the matcher
 * treats `event.key` as authoritative when it is an ASCII Latin letter. It falls
 * back to `event.code` when `event.key` is non-Latin (e.g. Cyrillic, Arabic,
 * or Greek) or is not a single usable character (e.g. `""`, `"Unidentified"`,
 * or `"Dead"`). This avoids matching a different remapped-layout Latin
 * character by physical key position while preserving single-alphabet
 * non-Latin layouts.
 *
 * The exported matcher also suppresses browser clipboard shortcuts using
 * either resolved `event.key` or physical `event.code`, so native editor
 * select-all/copy/paste/cut and undo/redo behavior wins across keyboard
 * layouts. Alt+clipboard-letter chords are not guarded. Shift+`a`/`c`/`v`/`x`
 * chords are not guarded, but Shift+`z` is guarded as native redo.
 *
 * @example
 * ```ts
 * const match = parseKeybinding('alt+cmd+k');
 * match({ metaKey: true, altKey: true, key: 'k' }) // true
 * match({ metaKey: true, key: 'k' })               // false (alt missing)
 *
 * const slash = parseKeybinding('/');
 * slash({ key: '/', shiftKey: true })  // true  (Shift needed on some layouts)
 * slash({ key: '/', shiftKey: false }) // true
 *
 * const digit = parseKeybinding('alt+cmd+1');
 * digit({ metaKey: true, altKey: true, key: '¡', code: 'Digit1' }) // true
 * digit({ metaKey: true, altKey: true, key: '1', code: 'Digit1' }) // true
 *
 * const upload = parseKeybinding('ctrl+u');
 * upload({ ctrlKey: true, key: 'u', code: 'KeyU' }) // true (English layout)
 * upload({ ctrlKey: true, key: 'v', code: 'KeyU' }) // false (remapped paste)
 * upload({ ctrlKey: true, key: 'г', code: 'KeyU' }) // true (Russian layout)
 * upload({ ctrlKey: true, key: 'Dead', code: 'KeyU' }) // true (fallback)
 *
 * const guardedUpload = parseKeybindings('ctrl+u');
 * guardedUpload({ ctrlKey: true, key: 'м', code: 'KeyV' }) // false (native paste)
 * ```
 */
function parseKeybinding(keybinding) {
    const { meta, ctrl, shift, alt, key } = parseKeybindingDescriptor(keybinding);
    const hasModifiers = meta || ctrl || shift || alt;
    return (event) => event.metaKey === meta &&
        event.ctrlKey === ctrl &&
        // For bare-key keybindings, ignore shiftKey — some keyboard layouts
        // require Shift to produce certain characters (e.g. `/` on AZERTY).
        (hasModifiers ? event.shiftKey === shift : true) &&
        event.altKey === alt &&
        // Match modifier keys via event.key, or fall back to event.code for:
        // - modifier+digit combos where modifiers transform the key (1 -> !, 1 -> ¡)
        // - modifier+letter combos on non-Latin layouts (u -> г on Russian, etc.)
        // ASCII Latin event.key remains authoritative for remapped layouts.
        (hasModifiers ? modifierKeyMatches(event, key) : event.key.toLowerCase() === key);
}
/**
 * Creates a matcher that matches if **any** of the provided keybinding strings
 * match. Useful for platform-branching shortcuts (e.g. Cmd+K on macOS **or**
 * Ctrl+I on Windows).
 *
 * @example
 * ```ts
 * const match = parseKeybindings('cmd+k', 'ctrl+i');
 * ```
 */
export function parseKeybindings(...keybindings) {
    const matchers = keybindings.map(parseKeybinding);
    return (event) => 
    // Guard native copy/paste/cut/select-all/undo before any shortcut can match
    // via Latin event.key or non-Latin physical event.code fallback.
    !isBrowserClipboardShortcut(event) && matchers.some((m) => m(event));
}
//# sourceMappingURL=keybinding.js.map